import { Button } from '@myog/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { API_URL, api, apiFailure } from '../lib/api-client.js';

/**
 * Page Settings.
 *
 * Contient l'export et la suppression de compte, qui ne sont pas des options
 * de confort mais les droits de portabilité et d'effacement du RGPD. Les
 * rendre disponibles en libre-service évite d'avoir à traiter ces demandes
 * par email — ce qui, passé quelques centaines de comptes, ne tiendrait pas.
 */

export function Settings() {
  const account = useQuery({
    queryKey: ['account'],
    queryFn: async () => {
      const response = await api.v1.account.$get();
      if (!response.ok) throw await apiFailure(response, 'Impossible de charger le profil.');
      return response.json();
    },
  });

  const [alertEmail, setAlertEmail] = useState<string | null>(null);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [confirming, setConfirming] = useState(false);

  const update = useMutation({
    mutationFn: async (value: string | null) => {
      const response = await api.v1.account.$patch({ json: { alertEmail: value } });
      if (!response.ok) throw await apiFailure(response, 'Enregistrement impossible.');
      return response.json();
    },
  });

  const remove = useMutation({
    mutationFn: async (email: string) => {
      const response = await api.v1.account.$delete({ json: { confirmEmail: email } });
      if (!response.ok)
        throw await apiFailure(response, "L'adresse saisie ne correspond pas au compte.");
      return response.json();
    },
    onSuccess: () => {
      window.location.href = 'https://myog.dev';
    },
  });

  if (account.isPending) {
    return <div className="h-64 animate-pulse rounded-lg bg-surface motion-reduce:animate-none" />;
  }

  if (account.isError) {
    return (
      <p
        role="alert"
        className="rounded-lg border border-danger/30 bg-danger/10 p-4 text-sm text-danger"
      >
        {account.error.message}
      </p>
    );
  }

  const user = account.data.user;
  const effectiveAlert = alertEmail ?? user.alertEmail ?? '';

  return (
    <div className="max-w-2xl space-y-10">
      <header>
        <h1 className="text-2xl font-semibold text-text">Réglages</h1>
      </header>

      <section aria-labelledby="profil" className="space-y-4">
        <h2 id="profil" className="text-lg font-semibold text-text">
          Profil
        </h2>
        <dl className="space-y-2 text-sm">
          <div className="flex gap-4">
            <dt className="w-36 shrink-0 text-text-muted">Email</dt>
            <dd className="font-mono text-text">{user.email}</dd>
          </div>
          <div className="flex gap-4">
            <dt className="w-36 shrink-0 text-text-muted">Nom</dt>
            <dd className="text-text">{user.name ?? '—'}</dd>
          </div>
          <div className="flex gap-4">
            <dt className="w-36 shrink-0 text-text-muted">Inscription</dt>
            <dd className="text-text">
              {new Date(user.createdAt * 1000).toLocaleDateString('fr-FR')}
            </dd>
          </div>
        </dl>
      </section>

      <section aria-labelledby="alertes" className="space-y-3">
        <h2 id="alertes" className="text-lg font-semibold text-text">
          Alertes de quota
        </h2>
        <p className="text-sm text-text-muted">
          Nous prévenons à 80 % et à 100 % du quota mensuel. Laissez vide pour utiliser l'adresse de
          votre compte.
        </p>

        <div className="flex gap-3">
          <div className="flex-1">
            <label htmlFor="alert-email" className="sr-only">
              Adresse de réception des alertes
            </label>
            <input
              id="alert-email"
              type="email"
              value={effectiveAlert}
              placeholder={user.email}
              onChange={(event) => setAlertEmail(event.target.value)}
              className="h-11 w-full rounded-md border border-border bg-bg px-3 text-sm text-text placeholder:text-text-subtle focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            />
          </div>
          <Button
            variant="secondary"
            loading={update.isPending}
            onClick={() => update.mutate(effectiveAlert === '' ? null : effectiveAlert)}
          >
            Enregistrer
          </Button>
        </div>

        <p aria-live="polite" className="text-sm">
          {update.isSuccess ? <span className="text-success">Enregistré.</span> : null}
          {update.isError ? <span className="text-danger">{update.error.message}</span> : null}
        </p>
      </section>

      <section aria-labelledby="donnees" className="space-y-3">
        <h2 id="donnees" className="text-lg font-semibold text-text">
          Vos données
        </h2>
        <p className="text-sm text-text-muted">
          Téléchargez l'intégralité des données que nous conservons à votre sujet, au format JSON.
        </p>
        {/* URL absolue : relative, le lien viserait Cloudflare Pages, qui
            renverrait index.html — un fichier « myog-export.json » contenant
            du HTML, sans la moindre erreur visible. */}
        <a
          href={`${API_URL}/v1/account/export`}
          download="myog-export.json"
          className="inline-flex h-11 items-center rounded-md border border-border px-4 text-sm font-medium text-text hover:border-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          Exporter mes données
        </a>
      </section>

      <section
        aria-labelledby="suppression"
        className="space-y-3 rounded-lg border border-danger/30 bg-danger/5 p-5"
      >
        <h2 id="suppression" className="text-lg font-semibold text-danger">
          Supprimer mon compte
        </h2>
        <p className="text-sm text-text-muted">
          Cette action est irréversible. Vos clés sont révoquées immédiatement, vos statistiques
          effacées, et toute requête utilisant vos clés échouera. Les données de facturation sont
          conservées dix ans par obligation comptable.
        </p>

        {confirming ? (
          <div className="space-y-3">
            <div>
              <label htmlFor="confirm-email" className="mb-1.5 block text-sm text-text">
                Saisissez <span className="font-mono text-text-muted">{user.email}</span> pour
                confirmer
              </label>
              <input
                id="confirm-email"
                type="email"
                value={confirmEmail}
                autoComplete="off"
                onChange={(event) => setConfirmEmail(event.target.value)}
                className="h-11 w-full rounded-md border border-danger/40 bg-bg px-3 text-sm text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger"
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="danger"
                loading={remove.isPending}
                disabled={confirmEmail.toLowerCase() !== user.email.toLowerCase()}
                onClick={() => remove.mutate(confirmEmail)}
              >
                Supprimer définitivement
              </Button>
              <Button variant="ghost" onClick={() => setConfirming(false)}>
                Annuler
              </Button>
            </div>

            {remove.isError ? (
              <p role="alert" className="text-sm text-danger">
                {remove.error.message}
              </p>
            ) : null}
          </div>
        ) : (
          <Button variant="danger" onClick={() => setConfirming(true)}>
            Supprimer mon compte
          </Button>
        )}
      </section>
    </div>
  );
}
